import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, ProtectedRoute } from "./auth/AuthContext";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import Marquee from "./components/Marquee";
import ProblemSection from "./components/ProblemSection";
import ServicesStrip from "./components/ServicesStrip";
import SolutionsBento from "./components/SolutionsBento";
import AviationSection from "./components/AviationSection";
import ProductsSection from "./components/ProductsSection";
import MarketCheckSection from "./components/MarketCheckSection";
import WhyAddAjemi from "./components/WhyAddAjemi";
import BackupSupply from "./components/BackupSupply";
import TankerSection from "./components/TankerSection";
import ManagedSupply from "./components/ManagedSupply";
import HowItWorks from "./components/HowItWorks";
import QuoteSection from "./components/QuoteSection";
import WhyAjemi from "./components/WhyAjemi";
import UseCases from "./components/UseCases";
import InternationalSection from "./components/InternationalSection";
import ProcurementDesk from "./components/ProcurementDesk";
import SupplierSection from "./components/SupplierSection";
import TrustSection from "./components/TrustSection";
import AboutSection from "./components/AboutSection";
import PresenceSection from "./components/PresenceSection";
import ContactSection from "./components/ContactSection";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Legal from "./pages/Legal";
import PortalLayout from "./portal/PortalLayout";
import CustomerDashboard from "./portal/customer/CustomerDashboard";
import NewRequest from "./portal/customer/NewRequest";
import RequestDetail from "./portal/customer/RequestDetail";
import SupplierDashboard from "./portal/supplier/SupplierDashboard";
import AdminDashboard from "./portal/admin/AdminDashboard";
import AdminRequestDetail from "./portal/admin/AdminRequestDetail";

function MarketingSite() {
  return (
    <div className="bg-ink text-white min-h-screen" data-testid="ajemi-app">
      <div className="noise-overlay" aria-hidden="true" />
      <Navbar />
      <main>
        <HeroSection />
        <Marquee />
        <ProblemSection />
        <WhyAddAjemi />
        <ServicesStrip />
        <SolutionsBento />
        <AviationSection />
        <ProductsSection />
        <MarketCheckSection />
        <TankerSection />
        <ManagedSupply />
        <HowItWorks />
        <QuoteSection />
        <WhyAjemi />
        <UseCases />
        <BackupSupply />
        <InternationalSection />
        <ProcurementDesk />
        <SupplierSection />
        <TrustSection />
        <AboutSection />
        <PresenceSection />
        <ContactSection />
      </main>
    </div>
  );
}

function LenisRoot() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== "/") return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [location.pathname]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LenisRoot />
        <Routes>
          <Route path="/" element={<MarketingSite />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/legal" element={<Legal />} />
          <Route element={<ProtectedRoute role="customer"><PortalLayout /></ProtectedRoute>}>
            <Route path="/portal" element={<CustomerDashboard />} />
            <Route path="/portal/new" element={<NewRequest />} />
            <Route path="/portal/requests/:id" element={<RequestDetail />} />
          </Route>
          <Route element={<ProtectedRoute role="supplier"><PortalLayout /></ProtectedRoute>}>
            <Route path="/supplier" element={<SupplierDashboard />} />
          </Route>
          <Route element={<ProtectedRoute role="admin"><PortalLayout /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/requests/:id" element={<AdminRequestDetail />} />
          </Route>
          <Route path="*" element={<MarketingSite />} />
        </Routes>
        <Toaster theme="dark" position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
